// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { SectionContainer, SectionTitle } from "@bg/App/Widgets/DesignSystem/DesignSystem.helpers";
import { headingTexts } from "@bg/App/Widgets/DesignSystem/DummyData/DummyData";
import { bgFontConfig as fontConfig } from "@bg/XDL/PreStyle/typography";
import { color } from "@bg/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps, GridItemStyle } from "@insite/mobius/GridItem";
import Link from "@insite/mobius/Link";
import ListItem, { OrderedList, UnorderedList } from "@insite/mobius/Lists";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import capitalize from "lodash/capitalize";
import React, { FC } from "react";
import { css } from "styled-components";

interface TypographyTabStyles {
    titleGridItem?: GridItemProps;
    gridItem?: GridItemProps;
    smallGridItem?: GridItemProps;
    formWrapper?: InjectableCss;
    linkWrapper?: InjectableCss;
    typographyWrapper?: GridContainerProps;
    viewTitle?: TypographyProps;
    headLine?: TypographyProps;
    headLineMobile?: TypographyProps;
    regular?: TypographyProps;
    small?: TypographyProps;
    xSmall?: TypographyProps;
    xxSmall?: TypographyProps;
}

const typogrphyTabStyles: TypographyTabStyles = {
    titleGridItem: {
        width: 12,
    },
    gridItem: {
        width: 12,
        css: css`
            flex-direction: column;
            padding-bottom: 15px;
        `,
    },
    smallGridItem: {
        width: [12, 12, 12, 6, 6],
        css: css`
            flex-wrap: wrap;
            padding-bottom: 15px;
        `,
    },
    formWrapper: {
        css: css`
            display: flex;
            justify-content: space-between;
            width: 100%;
            flex-wrap: wrap;
        `,
    },
    typographyWrapper: {
        gap: 24,
        css: css`
            ${GridItemStyle} {
                > * {
                    margin-bottom: 0;
                }
            }
        `,
    },
    viewTitle: {
        variant: "h3",
        css: css`
            padding-top: 8px;
            border-top: 1px solid ${color("common.border")};
            width: 100%;
        `,
    },
    linkWrapper: {
        css: css`
            background-color: #f2f2f2;
            width: 100%;
            padding: 12px;
            font-size: ${fontConfig.fontSize.regular}px;
            line-height: ${fontConfig.lineHeight.regular};
        `,
    },
    regular: {
        size: fontConfig.fontSize.regular,
        lineHeight: fontConfig.lineHeight.regular,
        weight: fontConfig.weight.regular,
        css: css`
            display: block;
            margin-bottom: 16px;
        `,
    },
    small: {
        size: fontConfig.fontSize.small,
        lineHeight: fontConfig.lineHeight.small,
        weight: fontConfig.weight.regular,
        css: css`
            display: block;
            margin-bottom: 14px;
        `,
    },
    xSmall: {
        size: fontConfig.fontSize.xSmall,
        lineHeight: fontConfig.lineHeight.xSmall,
        weight: fontConfig.weight.regular,
        css: css`
            display: block;
            margin-bottom: 12px;
        `,
    },
};

const styles = typogrphyTabStyles;

const bodyCopy = [
    {
        title: "Body Copy : Paragraph : Regular",
        style: styles.regular,
        fontInfo: `[Font] : [Weight] : Font-Size ${fontConfig.fontSize.regular}px : Line-Height: ${fontConfig.lineHeight.regular}`,
    },
    {
        title: "Body Copy : Paragraph : Small",
        style: styles.small,
        fontInfo: `[Font] : [Weight] : Font-Size ${fontConfig.fontSize.small}px : Line-Height:  ${fontConfig.lineHeight.small}`,
    },
    {
        title: "Body Copy : Paragraph : xSmall",
        style: styles.xSmall,
        fontInfo: `[Font] : [Weight] : Font-Size ${fontConfig.fontSize.xSmall}px : Line-Height: ${fontConfig.lineHeight.xSmall}`,
    },
];

const TypographyTab: FC = props => {
    return (
        <SectionContainer extendedStyles={{ gridContainer: { gap: 0 } }}>
            <GridItem {...styles.titleGridItem}>
                <SectionTitle>{translate("Font Families")}</SectionTitle>
            </GridItem>
            <GridItem {...styles.gridItem}>
                <StyledWrapper {...styles.formWrapper}>
                    <Typography>{translate("Open Sans")}</Typography>
                    {Object.entries(fontConfig.weight).map(([key, value]) => (
                        <Typography key={key} weight={value}>
                            {capitalize(key)}
                        </Typography>
                    ))}
                </StyledWrapper>
            </GridItem>
            <GridItem {...styles.gridItem}>
                <StyledWrapper {...styles.linkWrapper}>
                    <Typography>
                        Download [fonts Link] —&nbsp;
                        <code>
                            <a target="_blank" href={fontConfig.url} rel="noreferrer">
                                {fontConfig.url}
                            </a>
                        </code>
                    </Typography>
                </StyledWrapper>
            </GridItem>

            <GridItem {...styles.titleGridItem}>
                <SectionTitle>{translate("Headlines : Responsive")}</SectionTitle>
            </GridItem>
            <GridItem {...styles.gridItem}>
                <GridContainer {...styles.typographyWrapper}>
                    <GridItem width={3}></GridItem>
                    <GridItem width={3}>
                        <Typography {...styles.viewTitle}>{translate("Desktop")}</Typography>
                    </GridItem>
                    <GridItem width={3}>
                        <Typography {...styles.viewTitle}>{translate("Tablet")}</Typography>
                    </GridItem>
                    <GridItem width={3}>
                        <Typography {...styles.viewTitle}>{translate("Mobile")}</Typography>
                    </GridItem>

                    {Object.keys(headingTexts).map((key: any) => (
                        <React.Fragment key={key}>
                            <GridItem width={3}>
                                <Typography variant={key}>{translate(key.toUpperCase())}</Typography>
                            </GridItem>
                            <GridItem width={3}>{headingTexts[key].desktop}</GridItem>
                            <GridItem width={3}>{headingTexts[key].tablet || headingTexts[key].desktop}</GridItem>
                            <GridItem width={3}>{headingTexts[key].mobile || headingTexts[key].desktop}</GridItem>
                        </React.Fragment>
                    ))}
                </GridContainer>
            </GridItem>
            {bodyCopy.map(({ title, style, fontInfo }) => (
                <GridItem {...styles.gridItem} key={title}>
                    <SectionTitle>{translate(title)}</SectionTitle>
                    <Typography {...style}>{fontInfo}</Typography>
                    <Typography {...style}>
                        Lorem ipsum dolor sit amet, consectetur adipisicing elit, sed do eiusmod tempor incididunt ut
                        labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco
                        laboris nisi ut aliquip ex ea commodo consequat.
                    </Typography>
                    <Typography {...style}>
                        Lorem ipsum dolor sit amet, consectetur adipisicing elit, sed do eiusmod tempor incididunt ut
                        labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco
                        laboris nisi ut aliquip ex ea commodo consequat.
                    </Typography>
                    <Link typographyProps={{ ...style, weight: fontConfig.weight.bold }}>Text Link : Inactive</Link>
                    <Link typographyProps={{ ...style, weight: fontConfig.weight.bold }}>Text Link : Hover</Link>
                </GridItem>
            ))}

            <GridItem {...styles.gridItem}>
                <SectionTitle>{translate("Body Copy : Single")}</SectionTitle>
                <Typography {...styles.regular}>
                    {`Regular : [Font] : [Weight] : Font-Size ${fontConfig.fontSize.regular}px : Line-Height: ${fontConfig.lineHeight.regular}`}
                    <br /> [Example Second Line]
                </Typography>
                <Typography {...styles.small}>
                    {`Small : [Font] : [Weight] : Font-Size ${fontConfig.fontSize.small}px : Line-Height: ${fontConfig.lineHeight.small} `}
                    <br />
                    [Example Second Line]
                </Typography>
                <Typography {...styles.xSmall}>
                    {`XSmall : [Font] : [Weight] : Font-Size ${fontConfig.fontSize.xSmall}px : Line-Height: ${fontConfig.lineHeight.xSmall}`}
                    <br />
                    [Example Second Line]
                </Typography>
            </GridItem>

            <GridItem {...styles.gridItem}>
                <SectionTitle>Headers + Paragraph Spacing</SectionTitle>
                <Typography variant="h1">{translate("H1 Example")}</Typography>
                <Typography variant="h2">{translate("H2 Example")}</Typography>
                <Typography {...styles.regular}>
                    "Lorem ipsum dolor sit amet, consectetur adipisicing elit, sed do eiusmod tempor incididunt ut
                    labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris
                    nisi ut aliquip ex ea commodo consequat.",
                </Typography>
                <Typography {...styles.regular}>
                    "Lorem ipsum dolor sit amet, consectetur adipisicing elit, sed do eiusmod tempor incididunt ut
                    labore et dolore magna aliqua.",
                </Typography>
                <Typography variant="h3">{translate("H3 Example")}</Typography>
                <Typography {...styles.regular}>
                    "Lorem ipsum dolor sit amet, consectetur adipisicing elit, sed do eiusmod tempor incididunt ut
                    labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris
                    nisi ut aliquip ex ea commodo consequat.",
                </Typography>
                <Typography variant="h4">{translate("H4 Example")}</Typography>
                <Typography variant="h5">{translate("H5 Example")}</Typography>
                <Typography variant="h6">{translate("H6 Example")}</Typography>
            </GridItem>

            <GridItem {...styles.smallGridItem}>
                <SectionTitle>Bulleted Lists</SectionTitle>
                <StyledWrapper {...styles.formWrapper}>
                    <UnorderedList>
                        <ListItem>
                            Item 1
                            <UnorderedList>
                                <ListItem>Item 1.1</ListItem>
                                <ListItem>Item 1.2</ListItem>
                                <ListItem>Item 1.3</ListItem>
                            </UnorderedList>
                        </ListItem>
                        <ListItem>Item 2</ListItem>
                        <ListItem>Item 3</ListItem>
                        <ListItem>Item 4</ListItem>
                    </UnorderedList>
                </StyledWrapper>
            </GridItem>

            <GridItem {...styles.smallGridItem}>
                <SectionTitle>Numbered Lists</SectionTitle>
                <StyledWrapper {...styles.formWrapper}>
                    <OrderedList>
                        <ListItem>
                            Item 1
                            <OrderedList>
                                <ListItem>Item 1.1</ListItem>
                                <ListItem>Item 1.2</ListItem>
                                <ListItem>Item 1.3</ListItem>
                            </OrderedList>
                        </ListItem>
                        <ListItem>Item 2</ListItem>
                        <ListItem>Item 3</ListItem>
                        <ListItem>Item 4</ListItem>
                    </OrderedList>
                </StyledWrapper>
            </GridItem>
        </SectionContainer>
    );
};

export default TypographyTab;
